import { useEffect, useState, FormEvent, ChangeEvent } from "react";
import { FiX, FiBell, FiMessageSquare, FiInfo, FiZap } from "react-icons/fi";
import notifications from "../../../services/notifications";
import assistant from "../../../services/assistant";
import { toast } from "react-toastify";
import { Student } from "../../../types";

interface NotificationModalProps {
  isOpen: boolean;
  onClose: () => void;
  student: Student | null;
  onSuccess?: () => void;
  sendToAll?: boolean;
}

const initState = {
  title: "",
  description: "",
};

function NotificationModal({
  isOpen,
  onClose,
  student,
  onSuccess = () => {},
  sendToAll = false,
}: NotificationModalProps) {
  const [form, setForm] = useState(initState);
  const [loading, setLoading] = useState(false);
  const [onlyExpired, setOnlyExpired] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();

    if (!sendToAll && !student?.user?.id) {
      toast.error("Aluno não selecionado.");
      return;
    }

    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Por favor, preencha todos os campos.");
      return;
    }

    setLoading(true);

    if (sendToAll) {
      // Enviar para todos os alunos
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        only_expired: onlyExpired,
      };

      const data = await notifications.createAll(payload);

      setLoading(false);

      if (data) {
        toast.success(
          `Notificações enviadas com sucesso para ${data.count} aluno(s)!`,
        );
        onSuccess();
        onClose();
        setForm(initState);
      } else {
        toast.error(
          "Houve um problema ao enviar as notificações, por favor, tente novamente.",
        );
      }
    } else {
      // Enviar para aluno específico
      const payload = {
        title: form.title.trim(),
        description: form.description.trim(),
        user_id: student!.user.id,
      };

      const data = await notifications.create(payload);

      setLoading(false);

      if (data) {
        toast.success("Notificação enviada com sucesso!");
        onSuccess();
        onClose();
        setForm(initState);
      } else {
        toast.error(
          "Houve um problema ao enviar a notificação, por favor, tente novamente.",
        );
      }
    }
  }

  useEffect(() => {
    if (isOpen) {
      setForm(initState);
      setOnlyExpired(false);
    }
  }, [isOpen]);

  const handleChange = (target: { name: string; value: string }) => {
    setForm({ ...form, [target.name]: target.value });
  };

  async function generateMotivationalMessage() {
    setAiLoading(true);
    try {
      const result = await assistant.chat({
        assistant_id: 2,
        message: "Gere uma mensagem motivacional para enviar aos alunos",
      });

      if (result.success && result.response) {
        try {
          // A resposta vem no formato JSON: {"title": string, "description": string}
          const parsedResponse = JSON.parse(result.response);
          if (parsedResponse.title && parsedResponse.description) {
            setForm({
              title: parsedResponse.title,
              description: parsedResponse.description,
            });
            toast.success("Mensagem motivacional gerada com sucesso!");
          } else {
            toast.error("Formato de resposta inválido do assistente.");
          }
        } catch (parseError: unknown) {
          // Se não for JSON, tenta usar a resposta diretamente
          const errorMessage =
            parseError instanceof Error
              ? parseError.message
              : "Erro ao processar resposta do assistente.";
          console.error("Erro ao processar resposta:", parseError);
          toast.error(errorMessage);
        }
      } else {
        toast.error(
          result.error ||
            "Erro ao gerar mensagem motivacional. Tente novamente.",
        );
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Erro ao gerar mensagem motivacional. Tente novamente.";
      console.error("Erro ao gerar mensagem motivacional:", error);
      toast.error(errorMessage);
    } finally {
      setAiLoading(false);
    }
  }

  async function improveMessage() {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Preencha título e descrição antes de melhorar.");
      return;
    }

    setAiLoading(true);
    try {
      const message = `Melhore a seguinte mensagem de notificação. Título: "${form.title}". Descrição: "${form.description}". Retorne no formato JSON com "title" e "description".`;

      const result = await assistant.chat({
        assistant_id: 2,
        message: message,
      });

      if (result.success && result.response) {
        try {
          // A resposta vem no formato JSON: {"title": string, "description": string}
          const parsedResponse = JSON.parse(result.response);
          if (parsedResponse.title && parsedResponse.description) {
            setForm({
              title: parsedResponse.title,
              description: parsedResponse.description,
            });
            toast.success("Mensagem melhorada com sucesso!");
          } else {
            toast.error("Formato de resposta inválido do assistente.");
          }
        } catch (parseError: unknown) {
          // Se não for JSON, tenta usar a resposta diretamente
          const errorMessage =
            parseError instanceof Error
              ? parseError.message
              : "Erro ao processar resposta do assistente.";
          console.error("Erro ao processar resposta:", parseError);
          toast.error(errorMessage);
        }
      } else {
        toast.error(
          result.error || "Erro ao melhorar mensagem. Tente novamente.",
        );
      }
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Erro ao melhorar mensagem. Tente novamente.";
      console.error("Erro ao melhorar mensagem:", error);
      toast.error(errorMessage);
    } finally {
      setAiLoading(false);
    }
  }

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[999999] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4 backdrop-blur-sm overflow-y-auto">
      <div className="relative w-full max-w-full max-h-[95dvh] sm:max-h-[90vh] h-auto sm:max-w-2xl overflow-hidden rounded-t-2xl sm:rounded-2xl border border-stroke bg-white shadow-2xl dark:border-strokedark dark:bg-boxdark mt-auto sm:my-auto flex flex-col">
        {/* Header - Mobile First */}
        <div className="relative flex-shrink-0 border-b border-stroke bg-gradient-to-r from-purple-50 via-purple-50 to-purple-50 px-3 sm:px-6 py-3 sm:py-4 dark:border-strokedark dark:from-purple-950/30 dark:via-purple-950/30 dark:to-purple-950/30">
          <div className="absolute inset-0 bg-grid-pattern opacity-5" aria-hidden />

          <button
            type="button"
            onClick={onClose}
            className="absolute right-2 top-2.5 sm:right-4 sm:top-4 z-10 rounded-xl p-2.5 sm:p-2 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 flex items-center justify-center text-gray-500 outline-none transition-all hover:bg-white hover:text-gray-700 hover:shadow-sm focus-visible:ring-2 focus-visible:ring-primary dark:text-gray-400 dark:hover:bg-boxdark dark:hover:text-gray-200 touch-manipulation active:scale-95"
            aria-label="Fechar modal"
          >
            <FiX size={20} className="sm:w-6 sm:h-6" />
          </button>

          <div className="relative flex items-center gap-2.5 sm:gap-3 pr-12 sm:pr-0">
            <div className="rounded-xl bg-gradient-to-br from-purple-600 to-purple-600 p-2 sm:p-2.5 text-white shadow-sm flex-shrink-0">
              <FiBell size={18} className="sm:w-6 sm:h-6" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-sm sm:text-xl font-bold text-gray-900 dark:text-white leading-tight">
                {sendToAll
                  ? "Enviar Notificação para Todos"
                  : "Enviar Notificação"}
              </h3>
              <p className="mt-0.5 text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-tight">
                {(() => {
                  if (sendToAll) {
                    return "Enviar mensagem para todos os alunos vinculados";
                  }
                  if (student?.user?.name) {
                    return `Enviar mensagem para ${student.user.name}`;
                  }
                  return "Preencha os dados para enviar uma notificação";
                })()}
              </p>
            </div>
          </div>
        </div>

        {/* Form Content - Mobile First */}
        <form
          onSubmit={submit}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-3 sm:p-6"
        >
          <div className="space-y-3 sm:space-y-4">
            {/* Seção: Dados da Notificação */}
            <div className="rounded-xl sm:rounded-2xl border border-stroke bg-white p-3 sm:p-5 shadow-sm dark:border-strokedark dark:bg-boxdark">
              <div className="mb-3 sm:mb-4 flex items-center gap-2">
                <div className="h-0.5 sm:h-1 w-6 sm:w-8 rounded bg-purple-600 flex-shrink-0" />
                <h4 className="text-xs sm:text-sm font-bold uppercase tracking-wide text-gray-700 dark:text-gray-300">
                  Mensagem
                </h4>
              </div>

              <div className="space-y-3 sm:space-y-4">
                {/* Título */}
                <div>
                  <label
                    htmlFor="title"
                    className="mb-1 sm:mb-2 block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Título <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none z-10">
                      <FiBell size={16} className="sm:w-[18px] sm:h-[18px]" />
                    </span>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      required
                      maxLength={255}
                      value={form.title}
                      onChange={({ target }: ChangeEvent<HTMLInputElement>) =>
                        handleChange(target)
                      }
                      placeholder="Ex: Lembrete de treino"
                      className="w-full rounded-xl border border-stroke bg-gray-50 py-3 sm:py-2.5 pl-10 sm:pl-11 pr-3 sm:pr-4 text-sm sm:text-base outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 touch-manipulation min-h-[48px]"
                    />
                  </div>
                </div>

                {/* Descrição */}
                <div>
                  <label
                    htmlFor="description"
                    className="mb-1 sm:mb-2 block text-xs sm:text-sm font-semibold text-gray-700 dark:text-gray-300"
                  >
                    Mensagem <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 sm:left-4 top-3.5 sm:top-4 text-gray-400 pointer-events-none z-10">
                      <FiMessageSquare
                        size={16}
                        className="sm:w-[18px] sm:h-[18px]"
                      />
                    </span>
                    <textarea
                      id="description"
                      name="description"
                      required
                      rows={5}
                      value={form.description}
                      onChange={({
                        target,
                      }: ChangeEvent<HTMLTextAreaElement>) =>
                        handleChange(target)
                      }
                      placeholder={
                        sendToAll
                          ? "Digite a mensagem que deseja enviar para todos os alunos..."
                          : "Digite a mensagem que deseja enviar..."
                      }
                      className="w-full rounded-xl border border-stroke bg-gray-50 py-3 pl-10 sm:pl-11 pr-3 sm:pr-4 text-sm sm:text-base outline-none transition focus:border-purple-500 focus:bg-white focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input dark:text-white dark:focus:border-purple-500 resize-none touch-manipulation min-h-[120px] sm:min-h-[140px]"
                    />
                  </div>
                  {/* Botões de IA - Mobile First: empilham no mobile */}
                  <div className="mt-3 flex flex-col sm:flex-row flex-wrap gap-2 sm:justify-end">
                    <button
                      type="button"
                      onClick={generateMotivationalMessage}
                      disabled={aiLoading}
                      className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-purple-300 bg-purple-50 px-3 py-2.5 sm:py-1.5 text-xs sm:text-xs font-medium text-purple-700 transition-all hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed dark:border-purple-700 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 touch-manipulation min-h-[44px] sm:min-h-0 active:scale-[0.98]"
                    >
                      {aiLoading ? (
                        <>
                          <div className="h-3 w-3 flex-shrink-0 animate-spin rounded-full border-2 border-purple-600 border-t-transparent" />
                          <span>Gerando...</span>
                        </>
                      ) : (
                        <>
                          <FiZap size={14} className="flex-shrink-0" />
                          <span className="text-left">Criar mensagem motivacional</span>
                        </>
                      )}
                    </button>
                    {form.title.trim() && form.description.trim() && (
                      <button
                        type="button"
                        onClick={improveMessage}
                        disabled={aiLoading}
                        className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-purple-300 bg-purple-50 px-3 py-2.5 sm:py-1.5 text-xs font-medium text-purple-700 transition-all hover:bg-purple-100 disabled:opacity-50 disabled:cursor-not-allowed dark:border-purple-700 dark:bg-purple-900/30 dark:text-purple-300 dark:hover:bg-purple-900/50 touch-manipulation min-h-[44px] sm:min-h-0 active:scale-[0.98]"
                      >
                        {aiLoading ? (
                          <>
                            <div className="h-3 w-3 flex-shrink-0 animate-spin rounded-full border-2 border-purple-600 border-t-transparent" />
                            <span>Melhorando...</span>
                          </>
                        ) : (
                          <>
                            <FiZap size={14} className="flex-shrink-0" />
                            <span>Melhorar com IA</span>
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Checkbox - Mobile First */}
                {sendToAll && (
                  <div className="flex items-start gap-3 rounded-xl border border-purple-200 bg-purple-50/50 p-3 sm:p-3.5 dark:border-purple-800 dark:bg-purple-900/10">
                    <input
                      type="checkbox"
                      id="onlyExpired"
                      checked={onlyExpired}
                      onChange={(e) => setOnlyExpired(e.target.checked)}
                      className="mt-1 h-5 w-5 sm:h-4 sm:w-4 rounded border-stroke text-purple-600 focus:ring-2 focus:ring-purple-500/20 dark:border-strokedark dark:bg-form-input flex-shrink-0 touch-manipulation"
                    />
                    <label
                      htmlFor="onlyExpired"
                      className="flex-1 min-w-0 cursor-pointer text-xs sm:text-sm text-gray-700 dark:text-gray-300"
                    >
                      <span className="font-semibold block">
                        Notificar somente alunos com status vencidos
                      </span>
                      <p className="mt-0.5 text-xs text-gray-600 dark:text-gray-400">
                        Ao marcar esta opção, apenas alunos cujo plano está
                        vencido receberão a notificação.
                      </p>
                    </label>
                  </div>
                )}
              </div>
            </div>

            {/* Info Box - Mobile First */}
            <div className="relative overflow-hidden rounded-xl sm:rounded-2xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-purple-50 p-3 sm:p-4 dark:border-purple-800 dark:from-purple-900/20 dark:to-purple-900/20">
              <div className="absolute right-0 top-0 h-16 sm:h-24 w-16 sm:w-24 bg-gradient-to-br from-purple-500/10 to-purple-500/10 blur-sm"></div>

              <div className="relative flex items-start gap-2.5 sm:gap-3">
                <div className="rounded-lg sm:rounded-xl bg-purple-100 p-2 sm:p-2.5 dark:bg-purple-900/40 flex-shrink-0">
                  <FiInfo
                    className="text-purple-600 dark:text-purple-400"
                    size={18}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="mb-1 font-bold text-xs sm:text-sm text-purple-900 dark:text-purple-300">
                    Informação Importante
                  </h5>
                  <p className="text-xs sm:text-sm leading-relaxed text-purple-800 dark:text-purple-400">
                    {sendToAll
                      ? "As notificações serão enviadas diretamente para todos os seus alunos e aparecerão no aplicativo móvel. Certifique-se de que a mensagem está clara e objetiva."
                      : "A notificação será enviada diretamente para o aluno e aparecerá no aplicativo móvel. Certifique-se de que a mensagem está clara e objetiva."}
                  </p>
                  <div className="mt-2 sm:mt-2.5 flex flex-wrap gap-1.5 sm:gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                      ✓ Notificação push
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                      ✓ Envio instantâneo
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-purple-100 px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300">
                      ✓ Histórico salvo
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Footer - Mobile First: botões com área de toque adequada */}
        <div className="flex-shrink-0 sticky bottom-0 border-t border-stroke bg-white px-3 sm:px-6 py-3 sm:py-4 dark:border-strokedark dark:bg-boxdark shadow-[0_-4px_12px_-2px_rgba(0,0,0,0.08)] sm:shadow-none">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0">
            <p className="hidden sm:block text-xs sm:text-sm text-gray-600 dark:text-gray-400">
              Todos os campos marcados com{" "}
              <span className="text-red-500">*</span> são obrigatórios
            </p>
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-2.5 w-full sm:w-auto">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="w-full sm:w-auto order-2 sm:order-1 rounded-xl border-2 border-stroke px-4 sm:px-5 py-3 sm:py-2.5 text-sm sm:text-base font-semibold text-gray-700 transition-all hover:bg-gray-50 disabled:opacity-50 dark:border-strokedark dark:text-white dark:hover:bg-meta-4 touch-manipulation active:scale-[0.98] min-h-[48px] sm:min-h-0"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                onClick={submit}
                className="w-full sm:w-auto order-1 sm:order-2 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-purple-600 to-purple-600 px-5 sm:px-6 py-3 sm:py-2.5 text-sm sm:text-base font-semibold text-white shadow-sm transition-all hover:scale-105 hover:shadow-xl disabled:opacity-50 disabled:hover:scale-100 touch-manipulation active:scale-[0.98] min-h-[48px] sm:min-h-0"
              >
                {loading ? (
                  <>
                    <div className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    <span>Enviando...</span>
                  </>
                ) : (
                  <>
                    <FiBell size={18} className="sm:w-5 sm:h-5 flex-shrink-0" />
                    <span>{sendToAll ? "Enviar para Todos" : "Enviar Notificação"}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotificationModal;
